/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
// You can delete this file if you're not using it
import './src/utils/setupConsoleFilters';
import './src/shims/babel-polyfill-shim';
import { Amplify, Auth } from 'aws-amplify';
import awsConfig from "./src/aws-exports"
awsConfig.oauth.domain = process.env.GATSBY_COGNITO_OAUTH_DOMAIN;
// import core components
import { cartItemsService } from 'services';    
import { localstorageHelper } from 'helpers';
import UserContextProvider from "./src/contexts/UserContextProvider";

Amplify.configure(awsConfig)

document.addEventListener("snipcart.ready", cartItemsService.onCartReady);

const PILL_STYLE_NODE_ID = 'ss-snipcart-pill-style';
const ORDER_STYLE_NODE_ID = 'ss-snipcart-order-style';
const CHECKOUT_ITEM_SNAPSHOT_KEY = 'ss_checkout_item_snapshots';
const ORDER_HYDRATING_CLASS = 'ss-order-hydrating';
const ORDER_HYDRATION_FALLBACK_MS = 2500;

const isSnipcartOrderRoute = () => (
  typeof window !== 'undefined' && /#\/order\//i.test(window.location.hash || '')
);

const clearOrderHydrationFallback = () => {
  if (typeof window === 'undefined' || !window.__ssOrderHydrationFallback) return;
  window.clearTimeout(window.__ssOrderHydrationFallback);
  window.__ssOrderHydrationFallback = null;
};

const setOrderHydrating = active => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle(ORDER_HYDRATING_CLASS, Boolean(active));
};

const endOrderHydrationMask = () => {
  clearOrderHydrationFallback();
  setOrderHydrating(false);
};

const beginOrderHydrationMask = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!isSnipcartOrderRoute()) {
    endOrderHydrationMask();
    return;
  }
  if (document.querySelector('.ss-order-custom')) {
    endOrderHydrationMask();
    return;
  }
  setOrderHydrating(true);
  clearOrderHydrationFallback();
  window.__ssOrderHydrationFallback = window.setTimeout(
    endOrderHydrationMask,
    ORDER_HYDRATION_FALLBACK_MS
  );
};

const suppressSnipcartDuplicateCartNavigation = () => {
  if (typeof window === 'undefined' || window.__ssSuppressSnipcartDuplicateCartNavigation) return;
  window.__ssSuppressSnipcartDuplicateCartNavigation = true;
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const message = String(reason?.message || reason || '');
    if (/NavigationDuplicated/i.test(message) && /current location:\s*"\/cart"/i.test(message)) {
      event.preventDefault();
    }
  });
};
const PILL_CLASS = 'ss-pill-bs';
const PILL_BACKGROUND = 'linear-gradient(135deg, rgba(6,10,22,0.98), rgba(12,19,38,0.98) 48%, rgba(174,121,68,0.46))';
const PILL_BORDER = '1px solid rgba(210,179,122,0.66)';
const PILL_SHADOW = '0 14px 30px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.10)';
const PILL_SELECTOR = [
  '#snipcart button.snipcart-checkout',
  '#snipcart button.snipcart-cart__checkout-button',
  '#snipcart button.snipcart-mainaction',
  '#snipcart button.snipcart-submit',
  '#snipcart .snipcart-submit',
  '#snipcart .snipcart-button-primary',
  '#snipcart button[type="submit"]',
  '#snipcart input[type="submit"]',
  '#snipcart-main-container button.snipcart-checkout',
  '#snipcart-main-container button.snipcart-cart__checkout-button',
  '#snipcart-main-container button.snipcart-mainaction',
  '#snipcart-main-container button.snipcart-submit',
  '#snipcart-main-container .snipcart-submit',
  '#snipcart-main-container .snipcart-button-primary',
  '#snipcart-main-container button[type="submit"]',
  '#snipcart-main-container input[type="submit"]'
].join(', ');

const ensurePillStylesheet = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PILL_STYLE_NODE_ID)) return;

  const style = document.createElement('style');
  style.id = PILL_STYLE_NODE_ID;
  style.textContent = `
    #snipcart .${PILL_CLASS},
    #snipcart-main-container .${PILL_CLASS} {
      border-radius: 999px !important;
      padding: 0.75rem 1.2rem !important;
      background: ${PILL_BACKGROUND} !important;
      background-color: transparent !important;
      background-image: ${PILL_BACKGROUND} !important;
      color: #fff !important;
      border: ${PILL_BORDER} !important;
      box-shadow: ${PILL_SHADOW} !important;
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      font-weight: 600 !important;
      letter-spacing: 0.01em;
      transition: transform .12s ease, filter .12s ease !important;
    }
    #snipcart .${PILL_CLASS}:hover,
    #snipcart-main-container .${PILL_CLASS}:hover {
      filter: brightness(1.05) !important;
      transform: translateY(-1px) !important;
    }
    #snipcart .${PILL_CLASS}:active,
    #snipcart-main-container .${PILL_CLASS}:active {
      transform: translateY(0) !important;
    }
    #snipcart .${PILL_CLASS} .snipcart-cart-button__icon path,
    #snipcart-main-container .${PILL_CLASS} .snipcart-cart-button__icon path {
      fill: #fff !important;
    }
  `;
  document.head.appendChild(style);
};

const ensureOrderStylesheet = () => {
  if (typeof document === 'undefined') return;
  document.getElementById(ORDER_STYLE_NODE_ID)?.remove();

  const style = document.createElement('style');
  style.id = ORDER_STYLE_NODE_ID;
  style.textContent = `
    #snipcart,
    #snipcart-main-container {
      --ss-order-gold: #D2B37A;
      --ss-order-pale: rgba(255,255,255,0.94);
      --ss-order-soft: rgba(255,255,255,0.78);
      --ss-order-panel:
        radial-gradient(120% 90% at 8% 0%, rgba(210,179,122,0.12), rgba(5,9,20,0) 52%),
        linear-gradient(145deg, rgba(8,12,24,0.98), rgba(5,9,20,0.98) 52%, rgba(5,9,20,0.98));
    }

    #snipcart .snipcart-modal__container,
    #snipcart-main-container .snipcart-modal__container {
      background: #050914 !important;
      background-color: #050914 !important;
      background-image: none !important;
    }

    html.${ORDER_HYDRATING_CLASS} #snipcart .snipcart-order:not(.ss-order-frame),
    html.${ORDER_HYDRATING_CLASS} #snipcart-main-container .snipcart-order:not(.ss-order-frame),
    html.${ORDER_HYDRATING_CLASS} #snipcart [class*="order-confirmation"]:not(.ss-order-frame),
    html.${ORDER_HYDRATING_CLASS} #snipcart-main-container [class*="order-confirmation"]:not(.ss-order-frame) {
      opacity: 0 !important;
      pointer-events: none !important;
    }

    #snipcart .snipcart-order,
    #snipcart-main-container .snipcart-order,
    #snipcart [class*="order-confirmation"],
    #snipcart-main-container [class*="order-confirmation"] {
      width: min(860px, calc(100vw - 32px)) !important;
      max-width: 860px !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }

    #snipcart .snipcart-order__content,
    #snipcart-main-container .snipcart-order__content,
    #snipcart [class*="order-confirmation"],
    #snipcart-main-container [class*="order-confirmation"] {
      background: var(--ss-order-panel) !important;
      border: 1px solid rgba(210,179,122,0.48) !important;
      border-radius: 16px !important;
      overflow: visible !important;
      box-shadow: 0 24px 50px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.08) !important;
    }

    #snipcart .snipcart-order__header,
    #snipcart-main-container .snipcart-order__header,
    #snipcart .snipcart-order__summary,
    #snipcart-main-container .snipcart-order__summary,
    #snipcart .snipcart-order-summary,
    #snipcart-main-container .snipcart-order-summary,
    #snipcart .snipcart-order__billing,
    #snipcart-main-container .snipcart-order__billing,
    #snipcart .snipcart-order__payment,
    #snipcart-main-container .snipcart-order__payment,
    #snipcart .snipcart-order__payment-method,
    #snipcart-main-container .snipcart-order__payment-method,
    #snipcart .snipcart-order__fees,
    #snipcart-main-container .snipcart-order__fees,
    #snipcart .snipcart-summary-fees,
    #snipcart-main-container .snipcart-summary-fees,
    #snipcart [class*="order-confirmation"] [class*="header"],
    #snipcart-main-container [class*="order-confirmation"] [class*="header"],
    #snipcart [class*="order-confirmation"] [class*="summary"],
    #snipcart-main-container [class*="order-confirmation"] [class*="summary"],
    #snipcart [class*="order-confirmation"] [class*="billing"],
    #snipcart-main-container [class*="order-confirmation"] [class*="billing"],
    #snipcart [class*="order-confirmation"] [class*="payment"],
    #snipcart-main-container [class*="order-confirmation"] [class*="payment"] {
      margin: 0 !important;
      background: transparent !important;
      background-image: none !important;
      border-left: 0 !important;
      border-right: 0 !important;
      border-top: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }

    #snipcart .snipcart-order__header,
    #snipcart-main-container .snipcart-order__header,
    #snipcart [class*="order-confirmation"] [class*="header"] {
      padding: 38px 46px 30px !important;
      border-bottom: 1px solid rgba(210,179,122,0.18) !important;
    }

    #snipcart .snipcart-order__summary,
    #snipcart-main-container .snipcart-order__summary,
    #snipcart .snipcart-order-summary,
    #snipcart-main-container .snipcart-order-summary,
    #snipcart [class*="order-confirmation"] [class*="summary"] {
      padding: 32px 46px !important;
      border-bottom: 1px solid rgba(210,179,122,0.16) !important;
    }

    #snipcart .snipcart-order__billing,
    #snipcart-main-container .snipcart-order__billing,
    #snipcart [class*="order-confirmation"] [class*="billing"] {
      padding: 32px 46px !important;
      border-bottom: 1px solid rgba(210,179,122,0.16) !important;
    }

    #snipcart .snipcart-order__billing > *,
    #snipcart-main-container .snipcart-order__billing > *,
    #snipcart [class*="order-confirmation"] [class*="billing"] > * {
      background: rgba(255,255,255,0.035) !important;
      border: 1px solid rgba(210,179,122,0.34) !important;
      border-radius: 12px !important;
      padding: 24px 28px !important;
    }

    #snipcart .snipcart-order__payment,
    #snipcart-main-container .snipcart-order__payment,
    #snipcart .snipcart-order__payment-method,
    #snipcart-main-container .snipcart-order__payment-method,
    #snipcart [class*="order-confirmation"] [class*="payment"] {
      padding: 32px 46px !important;
      border-bottom: 1px solid rgba(210,179,122,0.16) !important;
    }

    #snipcart .snipcart-order__fees,
    #snipcart-main-container .snipcart-order__fees,
    #snipcart .snipcart-summary-fees,
    #snipcart-main-container .snipcart-summary-fees {
      padding: 28px 46px 38px !important;
      border: 0 !important;
    }

    #snipcart .snipcart-order__fees > *,
    #snipcart-main-container .snipcart-order__fees > *,
    #snipcart .snipcart-summary-fees > *,
    #snipcart-main-container .snipcart-summary-fees > * {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) max-content !important;
      align-items: baseline !important;
      column-gap: 28px !important;
      width: 100% !important;
      padding: 7px 0 !important;
      margin: 0 !important;
    }

    #snipcart .ss-order-frame .snipcart-order__summary-item,
    #snipcart-main-container .ss-order-frame .snipcart-order__summary-item,
    #snipcart .ss-order-frame .snipcart-order-summary__item,
    #snipcart-main-container .ss-order-frame .snipcart-order-summary__item {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) max-content max-content !important;
      align-items: center !important;
      gap: 18px !important;
      margin-top: 18px !important;
      padding: 14px 16px !important;
      background: rgba(255,255,255,0.04) !important;
      border: 1px solid rgba(210,179,122,0.40) !important;
      border-radius: 10px !important;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.08) !important;
    }

    #snipcart .snipcart-order *,
    #snipcart-main-container .snipcart-order *,
    #snipcart [class*="order-confirmation"] *,
    #snipcart-main-container [class*="order-confirmation"] *,
    #snipcart .snipcart-summary-fees *,
    #snipcart-main-container .snipcart-summary-fees * {
      color: var(--ss-order-soft) !important;
      letter-spacing: 0 !important;
      line-height: 1.45 !important;
    }

    #snipcart .snipcart-order h1,
    #snipcart-main-container .snipcart-order h1,
    #snipcart .snipcart-order h2,
    #snipcart-main-container .snipcart-order h2,
    #snipcart .snipcart-order h3,
    #snipcart-main-container .snipcart-order h3,
    #snipcart [class*="order-confirmation"] h1,
    #snipcart [class*="order-confirmation"] h2,
    #snipcart [class*="order-confirmation"] h3 {
      color: var(--ss-order-pale) !important;
      line-height: 1.22 !important;
    }

    #snipcart .snipcart-order svg,
    #snipcart-main-container .snipcart-order svg,
    #snipcart .snipcart-order svg *,
    #snipcart-main-container .snipcart-order svg *,
    #snipcart [class*="order-confirmation"] svg,
    #snipcart [class*="order-confirmation"] svg * {
      color: var(--ss-order-gold) !important;
      stroke: var(--ss-order-gold) !important;
    }

    #snipcart .snipcart-order [class*="total"],
    #snipcart-main-container .snipcart-order [class*="total"],
    #snipcart .snipcart-order [class*="amount"],
    #snipcart-main-container .snipcart-order [class*="amount"],
    #snipcart .snipcart-order [class*="price"],
    #snipcart-main-container .snipcart-order [class*="price"],
    #snipcart .snipcart-summary-fees [class*="total"],
    #snipcart-main-container .snipcart-summary-fees [class*="total"],
    #snipcart .snipcart-summary-fees [class*="amount"],
    #snipcart-main-container .snipcart-summary-fees [class*="amount"],
    #snipcart .snipcart-summary-fees [class*="price"],
    #snipcart-main-container .snipcart-summary-fees [class*="price"] {
      color: var(--ss-order-pale) !important;
      font-weight: 800 !important;
      white-space: nowrap !important;
      text-align: right !important;
    }

    #snipcart .snipcart-order__content,
    #snipcart-main-container .snipcart-order__content {
      padding: 0 !important;
    }

    #snipcart .snipcart-order__content > *,
    #snipcart-main-container .snipcart-order__content > *,
    #snipcart .snipcart-order__summary > *,
    #snipcart-main-container .snipcart-order__summary > *,
    #snipcart .snipcart-order-summary > *,
    #snipcart-main-container .snipcart-order-summary > *,
    #snipcart .snipcart-order__payment > *,
    #snipcart-main-container .snipcart-order__payment > *,
    #snipcart .snipcart-order__payment-method > *,
    #snipcart-main-container .snipcart-order__payment-method > *,
    #snipcart .snipcart-order__fees > *,
    #snipcart-main-container .snipcart-order__fees > * {
      background: transparent !important;
      background-color: transparent !important;
      background-image: none !important;
      box-shadow: none !important;
    }

    #snipcart .snipcart-order__summary-items,
    #snipcart-main-container .snipcart-order__summary-items,
    #snipcart .snipcart-order-summary__items,
    #snipcart-main-container .snipcart-order-summary__items,
    #snipcart .snipcart-order__billing > *,
    #snipcart-main-container .snipcart-order__billing > * {
      background: transparent !important;
      background-color: transparent !important;
      background-image: none !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      padding: 0 !important;
    }

    #snipcart .ss-order-frame .snipcart-order__summary-item > *,
    #snipcart-main-container .ss-order-frame .snipcart-order__summary-item > *,
    #snipcart .ss-order-frame .snipcart-order-summary__item > *,
    #snipcart-main-container .ss-order-frame .snipcart-order-summary__item > * {
      min-width: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      padding: 0 !important;
    }

    #snipcart .snipcart-summary-item,
    #snipcart-main-container .snipcart-summary-item {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) max-content max-content !important;
      align-items: center !important;
      gap: 12px !important;
      width: 100% !important;
      max-width: none !important;
    }

    #snipcart .snipcart-summary-item__name,
    #snipcart-main-container .snipcart-summary-item__name {
      display: block !important;
      min-width: 0 !important;
      width: auto !important;
      max-width: none !important;
      white-space: normal !important;
      overflow-wrap: normal !important;
      word-break: normal !important;
      line-height: 1.35 !important;
    }

    #snipcart .snipcart-summary-item__quantity,
    #snipcart-main-container .snipcart-summary-item__quantity,
    #snipcart .snipcart-summary-item__price,
    #snipcart-main-container .snipcart-summary-item__price {
      display: block !important;
      white-space: nowrap !important;
      word-break: normal !important;
      overflow-wrap: normal !important;
    }

    #snipcart .ss-order-frame,
    #snipcart-main-container .ss-order-frame {
      width: min(860px, calc(100vw - 32px)) !important;
      max-width: 860px !important;
      margin: 0 auto !important;
      overflow: visible !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: stretch !important;
      justify-content: flex-start !important;
      gap: 0 !important;
      background: var(--ss-order-panel) !important;
      background-color: transparent !important;
      border: 1px solid rgba(210,179,122,0.50) !important;
      border-radius: 16px !important;
      box-shadow: 0 24px 50px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.08) !important;
    }

    #snipcart .ss-order-frame > *,
    #snipcart-main-container .ss-order-frame > *,
    #snipcart .ss-order-section,
    #snipcart-main-container .ss-order-section {
      margin: 0 !important;
      background: transparent !important;
      background-color: transparent !important;
      background-image: none !important;
      border-left: 0 !important;
      border-right: 0 !important;
      border-top: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      position: static !important;
      transform: none !important;
      width: 100% !important;
      max-width: none !important;
      min-width: 0 !important;
      height: auto !important;
      min-height: 0 !important;
      float: none !important;
      flex: 0 0 auto !important;
    }

    #snipcart .ss-order-frame *:not(.ss-order-summary-item),
    #snipcart-main-container .ss-order-frame *:not(.ss-order-summary-item) {
      background: transparent !important;
      background-color: transparent !important;
      background-image: none !important;
      border-color: transparent !important;
      box-shadow: none !important;
      position: static !important;
      transform: none !important;
      float: none !important;
    }

    #snipcart .ss-order-frame,
    #snipcart-main-container .ss-order-frame {
      background: var(--ss-order-panel) !important;
      border-color: rgba(210,179,122,0.50) !important;
    }

    #snipcart .ss-order-section,
    #snipcart-main-container .ss-order-section {
      padding: 32px 46px !important;
      display: block !important;
      width: 100% !important;
      max-width: none !important;
      min-width: 0 !important;
    }

    #snipcart .ss-order-frame .ss-order-header,
    #snipcart-main-container .ss-order-frame .ss-order-header {
      order: 1 !important;
      box-sizing: border-box !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 38px 42px !important;
      border: 1px solid #B89A5E !important;
      border-radius: 14px 14px 0 0 !important;
      background: linear-gradient(135deg,#E3CFA0,#D2B37A,#B89A5E) !important;
      background-color: #D2B37A !important;
      background-image: linear-gradient(135deg,#E3CFA0,#D2B37A,#B89A5E) !important;
      box-shadow: 0 6px 16px rgba(0,0,0,0.35) !important;
      color: #2f2112 !important;
    }

    #snipcart .ss-order-header *,
    #snipcart-main-container .ss-order-header * {
      color: #2f2112 !important;
      border-color: rgba(90,62,29,0.24) !important;
    }

    #snipcart .ss-order-header h1,
    #snipcart-main-container .ss-order-header h1,
    #snipcart .ss-order-header h2,
    #snipcart-main-container .ss-order-header h2,
    #snipcart .ss-order-header h3,
    #snipcart-main-container .ss-order-header h3 {
      font-family: system-ui, -apple-system, sans-serif !important;
      font-size: 28px !important;
      text-transform: uppercase !important;
      letter-spacing: 0.6px !important;
      color: #5A3B12 !important;
      opacity: 0.95 !important;
      font-weight: 800 !important;
      line-height: 1.2 !important;
      margin-bottom: 0 !important;
    }

    #snipcart .ss-order-header svg,
    #snipcart-main-container .ss-order-header svg,
    #snipcart .ss-order-header svg *,
    #snipcart-main-container .ss-order-header svg * {
      color: #5f451f !important;
      stroke: #5f451f !important;
    }

    #snipcart .ss-order-frame .ss-order-header svg,
    #snipcart-main-container .ss-order-frame .ss-order-header svg {
      display: none !important;
    }

    #snipcart .ss-order-header-message,
    #snipcart-main-container .ss-order-header-message {
      margin: 6px 0 0 !important;
      max-width: 720px !important;
      font-family: system-ui, -apple-system, sans-serif !important;
      color: #3E2B11 !important;
      font-size: 12px !important;
      line-height: 1.45 !important;
      font-weight: 500 !important;
      opacity: 0.85 !important;
      white-space: normal !important;
    }

    #snipcart .ss-order-header-message a,
    #snipcart-main-container .ss-order-header-message a {
      color: #5A3B12 !important;
      font-weight: 800 !important;
      text-decoration: underline !important;
      text-underline-offset: 3px !important;
      white-space: nowrap !important;
      display: inline !important;
    }

    #snipcart .ss-order-invoice-number,
    #snipcart-main-container .ss-order-invoice-number {
      color: #050914 !important;
      font-weight: 850 !important;
    }

    #snipcart .ss-order-invoice-line,
    #snipcart-main-container .ss-order-invoice-line {
      display: inline-flex !important;
      align-items: baseline !important;
      gap: 4px !important;
      margin-top: 6px !important;
      color: #050914 !important;
      font-family: system-ui, -apple-system, sans-serif !important;
      font-size: 15px !important;
      line-height: 1.35 !important;
      font-weight: 500 !important;
      white-space: nowrap !important;
    }

    #snipcart .ss-order-invoice-line *,
    #snipcart-main-container .ss-order-invoice-line * {
      color: #050914 !important;
      font-family: system-ui, -apple-system, sans-serif !important;
      font-size: 15px !important;
      white-space: nowrap !important;
    }

    #snipcart .ss-order-header .ss-order-invoice-line,
    #snipcart-main-container .ss-order-header .ss-order-invoice-line,
    #snipcart .ss-order-header .ss-order-invoice-line *,
    #snipcart-main-container .ss-order-header .ss-order-invoice-line * {
      color: #050914 !important;
    }

    #snipcart .ss-order-invoice-native-hidden,
    #snipcart-main-container .ss-order-invoice-native-hidden {
      display: none !important;
    }

    #snipcart .ss-order-summary,
    #snipcart-main-container .ss-order-summary {
      order: 2 !important;
    }

    #snipcart .ss-order-billing,
    #snipcart-main-container .ss-order-billing {
      order: 3 !important;
    }

    #snipcart .ss-order-payment,
    #snipcart-main-container .ss-order-payment {
      order: 4 !important;
    }

    #snipcart .ss-order-totals,
    #snipcart-main-container .ss-order-totals {
      order: 5 !important;
    }

    #snipcart .ss-order-section + .ss-order-section,
    #snipcart-main-container .ss-order-section + .ss-order-section {
      border-top: 1px solid rgba(210,179,122,0.16) !important;
      border-top-color: rgba(210,179,122,0.16) !important;
    }

    #snipcart .ss-order-frame .ss-order-billing,
    #snipcart-main-container .ss-order-frame .ss-order-billing {
      background: transparent !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }

    #snipcart .ss-order-frame .ss-order-billing *,
    #snipcart-main-container .ss-order-frame .ss-order-billing * {
      background: transparent !important;
      box-shadow: none !important;
    }

    #snipcart .ss-order-summary h1,
    #snipcart-main-container .ss-order-summary h1,
    #snipcart .ss-order-summary h2,
    #snipcart-main-container .ss-order-summary h2,
    #snipcart .ss-order-summary h3,
    #snipcart-main-container .ss-order-summary h3,
    #snipcart .ss-order-billing h1,
    #snipcart-main-container .ss-order-billing h1,
    #snipcart .ss-order-billing h2,
    #snipcart-main-container .ss-order-billing h2,
    #snipcart .ss-order-billing h3,
    #snipcart-main-container .ss-order-billing h3,
    #snipcart .ss-order-payment h1,
    #snipcart-main-container .ss-order-payment h1,
    #snipcart .ss-order-payment h2,
    #snipcart-main-container .ss-order-payment h2,
    #snipcart .ss-order-payment h3,
    #snipcart-main-container .ss-order-payment h3 {
      margin: 0 0 22px !important;
    }

    #snipcart .ss-order-summary-item,
    #snipcart-main-container .ss-order-summary-item {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) max-content max-content !important;
      align-items: center !important;
      gap: 18px !important;
      margin-top: 18px !important;
      padding: 14px 16px !important;
      background: rgba(255,255,255,0.04) !important;
      border: 1px solid rgba(210,179,122,0.40) !important;
      border-color: rgba(210,179,122,0.40) !important;
      border-radius: 10px !important;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.08) !important;
      width: 100% !important;
      max-width: none !important;
      min-width: 0 !important;
    }

    #snipcart .ss-order-summary-item > *,
    #snipcart-main-container .ss-order-summary-item > * {
      min-width: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      padding: 0 !important;
    }

    #snipcart .ss-order-totals,
    #snipcart-main-container .ss-order-totals {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) max-content !important;
      gap: 10px 32px !important;
      align-items: baseline !important;
      padding: 28px 46px 38px !important;
      background: transparent !important;
      border-top: 1px solid rgba(210,179,122,0.16) !important;
      border-top-color: rgba(210,179,122,0.16) !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }

    #snipcart .ss-order-totals > *,
    #snipcart-main-container .ss-order-totals > * {
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
      min-width: 0 !important;
    }

    #snipcart .ss-order-frame > :not(.ss-order-header):not(.ss-order-custom),
    #snipcart-main-container .ss-order-frame > :not(.ss-order-header):not(.ss-order-custom) {
      display: none !important;
    }

    #snipcart .ss-order-custom,
    #snipcart-main-container .ss-order-custom {
      order: 2 !important;
      display: flex !important;
      flex-direction: column !important;
      width: 100% !important;
      padding: 0 !important;
      background:
        radial-gradient(120% 90% at 0% 0%, rgba(210,179,122,0.22), rgba(210,179,122,0) 46%),
        linear-gradient(135deg, rgba(45,40,38,0.98), rgba(8,12,24,0.98) 48%, rgba(82,52,34,0.96)) !important;
      border: 0 !important;
      box-shadow: none !important;
    }

    #snipcart .ss-order-custom > .ss-order-custom-section:first-child,
    #snipcart-main-container .ss-order-custom > .ss-order-custom-section:first-child {
      border-top: 1px solid rgba(210,179,122,0.16) !important;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.025) !important;
    }

    #snipcart .ss-order-frame .ss-order-custom-section,
    #snipcart-main-container .ss-order-frame .ss-order-custom-section {
      padding: 32px 46px !important;
      border-top: 1px solid rgba(210,179,122,0.16) !important;
      border-top-color: rgba(210,179,122,0.16) !important;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.025) !important;
      background: transparent !important;
      font-size: 0.96rem !important;
    }

    #snipcart .ss-order-frame .ss-order-total-section,
    #snipcart-main-container .ss-order-frame .ss-order-total-section {
      margin: 0 !important;
      width: 100% !important;
      padding: 32px 46px 38px !important;
      background:
        radial-gradient(115% 120% at 18% 0%, rgba(210,179,122,0.12), rgba(210,179,122,0) 46%),
        linear-gradient(135deg, rgba(13,22,43,0.96), rgba(7,13,28,0.98) 52%, rgba(16,25,47,0.94)) !important;
      border: 0 !important;
      border-top: 1px solid rgba(210,179,122,0.18) !important;
      border-radius: 0 0 14px 14px !important;
      box-shadow:
        inset 0 1px 0 rgba(255,255,255,0.06),
        inset 0 24px 44px rgba(210,179,122,0.035) !important;
    }

    #snipcart .ss-order-custom-heading,
    #snipcart-main-container .ss-order-custom-heading {
      display: flex !important;
      align-items: center !important;
      gap: 10px !important;
      margin: 0 0 24px !important;
      color: var(--ss-order-pale) !important;
      font-weight: 800 !important;
      font-size: 1.2rem !important;
      line-height: 1.2 !important;
    }

    #snipcart .ss-order-icon,
    #snipcart-main-container .ss-order-icon {
      width: 22px !important;
      height: 22px !important;
      color: var(--ss-order-gold) !important;
      stroke: var(--ss-order-gold) !important;
      fill: none !important;
      flex: 0 0 auto !important;
    }

    #snipcart .ss-order-item,
    #snipcart-main-container .ss-order-item {
      display: grid !important;
      grid-template-columns: minmax(128px, 150px) minmax(0, 1fr) max-content !important;
      gap: 24px !important;
      align-items: start !important;
      padding: 22px !important;
      background:
        linear-gradient(135deg, rgba(255,255,255,0.035), rgba(210,179,122,0.028) 42%, rgba(5,9,20,0.12)) !important;
      border: 1px solid rgba(210,179,122,0.16) !important;
      border-radius: 12px !important;
      box-shadow:
        inset 0 1px 0 rgba(255,255,255,0.07),
        0 16px 34px rgba(0,0,0,0.18) !important;
    }

    #snipcart .ss-order-item + .ss-order-item,
    #snipcart-main-container .ss-order-item + .ss-order-item {
      margin-top: 18px !important;
      padding-top: 24px !important;
      border-top: 1px solid rgba(210,179,122,0.24) !important;
    }

    #snipcart .ss-order-thumb,
    #snipcart-main-container .ss-order-thumb {
      width: 100% !important;
      height: 212px !important;
      object-fit: cover !important;
      object-position: top center !important;
      border: 1px solid rgba(210,179,122,0.38) !important;
      border-radius: 12px !important;
      background: rgba(5,9,20,0.72) !important;
      box-shadow:
        0 14px 26px rgba(0,0,0,0.26),
        inset 0 1px 0 rgba(255,255,255,0.08) !important;
    }

    #snipcart .ss-order-thumb-placeholder,
    #snipcart-main-container .ss-order-thumb-placeholder {
      display: grid !important;
      place-items: center !important;
      color: rgba(255,255,255,0.44) !important;
      font-size: 0.78rem !important;
    }

    #snipcart .ss-order-item-title,
    #snipcart-main-container .ss-order-item-title {
      margin: 0 0 12px !important;
      color: var(--ss-order-pale) !important;
      font-size: 1.02rem !important;
      font-weight: 800 !important;
      line-height: 1.25 !important;
    }

    #snipcart .ss-order-detail-grid,
    #snipcart-main-container .ss-order-detail-grid {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 12px !important;
    }

    #snipcart .ss-order-detail-box,
    #snipcart-main-container .ss-order-detail-box {
      min-width: 0 !important;
      padding: 12px !important;
      background: rgba(5,9,20,0.26) !important;
      border: 1px solid rgba(210,179,122,0.18) !important;
      border-radius: 8px !important;
    }

    #snipcart .ss-order-detail-label,
    #snipcart-main-container .ss-order-detail-label {
      display: block !important;
      margin-bottom: 6px !important;
      color: rgba(210,179,122,0.88) !important;
      font-size: 0.76rem !important;
      font-weight: 800 !important;
      text-transform: uppercase !important;
      letter-spacing: 0.04em !important;
    }

    #snipcart .ss-order-detail-value,
    #snipcart-main-container .ss-order-detail-value {
      color: rgba(255,255,255,0.84) !important;
      font-size: 0.9rem !important;
      line-height: 1.45 !important;
      white-space: pre-line !important;
    }

    #snipcart .ss-order-detail-box:nth-child(2) .ss-order-detail-value,
    #snipcart-main-container .ss-order-detail-box:nth-child(2) .ss-order-detail-value {
      white-space: pre-line !important;
      overflow-x: visible !important;
      padding-bottom: 2px !important;
    }

    #snipcart .ss-order-options,
    #snipcart-main-container .ss-order-options {
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 8px !important;
      margin-top: 12px !important;
    }

    #snipcart .ss-order-chip,
    #snipcart-main-container .ss-order-chip {
      display: inline-flex !important;
      align-items: center !important;
      min-height: 30px !important;
      padding: 5px 9px !important;
      background: rgba(210,179,122,0.10) !important;
      border: 1px solid rgba(210,179,122,0.22) !important;
      border-radius: 8px !important;
      color: var(--ss-order-soft) !important;
      font-size: 0.82rem !important;
      font-weight: 700 !important;
    }

    #snipcart .ss-order-item-price,
    #snipcart-main-container .ss-order-item-price {
      min-width: 92px !important;
      text-align: right !important;
      color: var(--ss-order-pale) !important;
      font-weight: 900 !important;
      white-space: nowrap !important;
    }

    #snipcart .ss-order-price-stack,
    #snipcart-main-container .ss-order-price-stack {
      display: flex !important;
      flex-direction: column !important;
      justify-items: end !important;
      align-items: flex-end !important;
      gap: 6px !important;
      min-width: 116px !important;
    }

    #snipcart .ss-order-price-label,
    #snipcart-main-container .ss-order-price-label {
      color: rgba(255,255,255,0.62) !important;
      font-size: 0.78rem !important;
      font-weight: 700 !important;
    }

    #snipcart .ss-order-card-method,
    #snipcart-main-container .ss-order-card-method {
      display: inline-flex !important;
      align-items: center !important;
      gap: 12px !important;
      color: rgba(255,255,255,0.88) !important;
      font-weight: 700 !important;
    }

    #snipcart .ss-order-card-badge,
    #snipcart-main-container .ss-order-card-badge {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      min-width: 46px !important;
      height: 28px !important;
      padding: 0 8px !important;
      border: 1px solid rgba(210,179,122,0.32) !important;
      border-radius: 6px !important;
      color: var(--ss-order-gold) !important;
      font-size: 0.78rem !important;
      font-weight: 900 !important;
      letter-spacing: 0.02em !important;
      background: rgba(5,9,20,0.34) !important;
    }

    #snipcart .ss-order-two-col,
    #snipcart-main-container .ss-order-two-col {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 28px !important;
    }

    #snipcart .ss-order-total-row,
    #snipcart-main-container .ss-order-total-row {
      display: grid !important;
      grid-template-columns: max-content max-content !important;
      justify-content: end !important;
      gap: 18px !important;
      align-items: baseline !important;
      padding: 9px 0 !important;
      color: rgba(255,255,255,0.82) !important;
      font-size: 1.05rem !important;
      font-weight: 650 !important;
    }

    #snipcart .ss-order-total-row strong,
    #snipcart-main-container .ss-order-total-row strong {
      color: var(--ss-order-pale) !important;
      font-size: 1.16rem !important;
      font-weight: 900 !important;
      white-space: nowrap !important;
      text-align: right !important;
    }

    #snipcart .ss-order-frame > .ss-order-custom,
    #snipcart-main-container .ss-order-frame > .ss-order-custom {
      background-color: #241d1d !important;
      background-image:
        radial-gradient(120% 90% at 0% 0%, rgba(210,179,122,0.28), rgba(210,179,122,0) 48%),
        linear-gradient(135deg, rgba(48,39,36,0.98) 0%, rgba(8,12,24,0.98) 46%, rgba(92,56,35,0.96) 100%) !important;
      background-repeat: no-repeat !important;
      background-size: 100% 100% !important;
    }

    #snipcart .ss-order-frame > .ss-order-custom > .ss-order-custom-section:not(.ss-order-total-section),
    #snipcart-main-container .ss-order-frame > .ss-order-custom > .ss-order-custom-section:not(.ss-order-total-section) {
      background: transparent !important;
      background-color: transparent !important;
      background-image: none !important;
    }

    @media (max-width: 720px) {
      #snipcart .snipcart-order,
      #snipcart-main-container .snipcart-order,
      #snipcart [class*="order-confirmation"],
      #snipcart-main-container [class*="order-confirmation"] {
        width: calc(100vw - 20px) !important;
      }

      #snipcart .snipcart-order__header,
      #snipcart-main-container .snipcart-order__header,
      #snipcart .snipcart-order__summary,
      #snipcart-main-container .snipcart-order__summary,
      #snipcart .snipcart-order__billing,
      #snipcart-main-container .snipcart-order__billing,
      #snipcart .snipcart-order__payment,
      #snipcart-main-container .snipcart-order__payment,
      #snipcart .snipcart-order__fees,
      #snipcart-main-container .snipcart-order__fees,
      #snipcart .snipcart-summary-fees,
      #snipcart-main-container .snipcart-summary-fees {
        padding-left: 22px !important;
        padding-right: 22px !important;
      }

      #snipcart .ss-order-frame,
      #snipcart-main-container .ss-order-frame {
        width: calc(100vw - 20px) !important;
      }

      #snipcart .ss-order-header,
      #snipcart-main-container .ss-order-header {
        width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        padding: 28px 24px !important;
      }

      #snipcart .ss-order-section,
      #snipcart-main-container .ss-order-section,
      #snipcart .ss-order-totals,
      #snipcart-main-container .ss-order-totals,
      #snipcart .ss-order-custom-section,
      #snipcart-main-container .ss-order-custom-section {
        padding-left: 22px !important;
        padding-right: 22px !important;
      }

      #snipcart .ss-order-item,
      #snipcart-main-container .ss-order-item,
      #snipcart .ss-order-detail-grid,
      #snipcart-main-container .ss-order-detail-grid,
      #snipcart .ss-order-two-col,
      #snipcart-main-container .ss-order-two-col {
        grid-template-columns: 1fr !important;
      }

      #snipcart .ss-order-detail-box:nth-child(2) .ss-order-detail-value,
      #snipcart-main-container .ss-order-detail-box:nth-child(2) .ss-order-detail-value {
        white-space: pre-line !important;
        overflow-x: visible !important;
      }

      #snipcart .ss-order-item,
      #snipcart-main-container .ss-order-item {
        gap: 16px !important;
        padding: 16px !important;
      }

      #snipcart .ss-order-thumb,
      #snipcart-main-container .ss-order-thumb {
        width: 100% !important;
        height: auto !important;
        max-height: none !important;
        aspect-ratio: auto !important;
        object-fit: contain !important;
        object-position: center !important;
      }

      #snipcart .ss-order-thumb-placeholder,
      #snipcart-main-container .ss-order-thumb-placeholder {
        min-height: 220px !important;
      }

      #snipcart .ss-order-item-price,
      #snipcart-main-container .ss-order-item-price {
        text-align: left !important;
      }

      #snipcart .ss-order-price-stack,
      #snipcart-main-container .ss-order-price-stack {
        width: 100% !important;
        min-width: 0 !important;
        flex-direction: row !important;
        justify-content: space-between !important;
        align-items: center !important;
      }
    }
  `;
  document.head.appendChild(style);
};

const applyInlinePillStyles = (button) => {
  if (!button) return;
  try {
    button.style.setProperty('border-radius', '999px', 'important');
    button.style.setProperty('padding', '0.75rem 1.2rem', 'important');
    button.style.setProperty('background', PILL_BACKGROUND, 'important');
    button.style.setProperty('background-color', 'transparent', 'important');
    button.style.setProperty('background-image', PILL_BACKGROUND, 'important');
    button.style.setProperty('color', '#ffffff', 'important');
    button.style.setProperty('border', PILL_BORDER, 'important');
    button.style.setProperty('box-shadow', PILL_SHADOW, 'important');
    button.style.setProperty('display', 'inline-flex', 'important');
    button.style.setProperty('align-items', 'center', 'important');
    button.style.setProperty('justify-content', 'center', 'important');
    button.style.setProperty('gap', '0.4rem', 'important');
    button.style.setProperty('font-weight', '600', 'important');
    button.style.setProperty('letter-spacing', '0.01em', 'important');
    button.style.setProperty('transition', 'transform .12s ease, filter .12s ease', 'important');
  } catch (err) {
    // swallow styling errors silently
  }
};

const applyPillStyles = () => {
  if (typeof document === 'undefined') return;
  ensurePillStylesheet();
  const buttonList = new Set();
  document.querySelectorAll(PILL_SELECTOR).forEach((btn) => buttonList.add(btn));

  // heuristic: catch translated versions of "Place Order" or similar CTAs
  const keywordMatchers = ['place order', 'complete order', 'confirm order', 'pay now', 'finish order'];
  const textualButtons = document.querySelectorAll('#snipcart-main-container button, #snipcart button');
  textualButtons.forEach((btn) => {
    const text = (btn.textContent || '').toLowerCase();
    if (keywordMatchers.some((kw) => text.includes(kw))) {
      buttonList.add(btn);
    }
  });

  if (!buttonList.size) return;

  buttonList.forEach((btn) => {
    if (!btn.classList.contains('snipcart-cart-button')) {
      btn.classList.add('snipcart-cart-button');
    }
    if (!btn.classList.contains(PILL_CLASS)) {
      btn.classList.add(PILL_CLASS);
    }
    if (btn.tagName === 'INPUT') {
      // convert input submit to behave like button via inline styles
      btn.style.setProperty('appearance', 'none', 'important');
    }
    applyInlinePillStyles(btn);
  });
};

const hideSnipcartEmptyBackButton = () => {
  if (typeof document === 'undefined') return;
  const root = document.getElementById('snipcart-main-container') || document.getElementById('snipcart');
  if (!root) return;

  root.querySelectorAll('button, a').forEach((element) => {
    const text = normalizeText(element.textContent).toLowerCase();
    if (text === 'back to store' || text.includes('back to store')) {
      element.style.setProperty('display', 'none', 'important');
      element.style.setProperty('visibility', 'hidden', 'important');
    }
  });
};

const attachSnipcartObserver = () => {
  if (typeof MutationObserver === 'undefined') return;
  if (window.__ssSnipcartPillObserver) return;

  const target = document.getElementById('snipcart-main-container')
    || document.getElementById('snipcart')
    || document.body;
  if (!target) return;

  const observer = new MutationObserver(() => {
    ensureOrderStylesheet();
    applyPillStyles();
    hideSnipcartEmptyBackButton();
  });

  observer.observe(target, { childList: true, subtree: true });
  window.__ssSnipcartPillObserver = observer;
};

const registerSnipcartPaymentStyles = (attempt = 0) => {
  if (typeof window === 'undefined') return;
  const customization = window.Snipcart?.api?.theme?.customization;
  if (!customization?.registerPaymentFormCustomization) {
    if (attempt < 20) {
      setTimeout(() => registerSnipcartPaymentStyles(attempt + 1), 250);
    }
    return;
  }

  customization.registerPaymentFormCustomization({
    input: {
      backgroundColor: '#b9a16e',
      border: '1px solid rgba(210,179,122,0.58)',
      color: '#050914',
      fontSize: '16px',
      placeholder: {
        color: 'rgba(5,9,20,0.62)',
      },
    },
    label: {
      color: 'rgba(255,255,255,0.9)',
      fontSize: '14px',
    },
  });
};

const markSnipcartPaymentPanels = () => {
  if (typeof document === 'undefined') return;

  document
    .querySelectorAll('#snipcart .snipcart-payment-card-form, #snipcart-main-container .snipcart-payment-card-form')
    .forEach((cardForm) => {
      cardForm.classList.add('ss-payment-card-form');

      const host = cardForm.parentElement;
      if (host) {
        host.classList.add('ss-payment-card-host');
      }

      const panel = cardForm.closest('.snipcart-form__section, .snipcart-payment');
      if (panel) {
        panel.classList.add('ss-payment-panel');
      }
    });
};

const normalizeText = (value) => (value || '').replace(/\s+/g, ' ').trim();

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const formatOrderMoney = (value) => {
  if (typeof value === 'string' && value.trim()) {
    const trimmed = value.trim();
    if (/^(sgd|\$)/i.test(trimmed)) return trimmed;
  }
  const num = Number.parseFloat(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(num) ? `SGD ${num.toFixed(2)}` : '';
};

const parseOrderMoney = (value) => {
  const num = Number.parseFloat(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(num) ? num : null;
};

const normalizeOrderImage = (value) => {
  if (typeof value !== 'string' || !value.trim()) return '';
  const raw = value.trim();
  if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:image') || raw.startsWith('blob:')) return raw;
  const base = process.env.GATSBY_S3_BUCKET_BASE_URL || '';
  return base ? `${base.replace(/\/$/, '')}/${raw.replace(/^\/+/, '')}` : raw;
};

const getOrderImageValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    for (const entry of value) {
      const resolved = getOrderImageValue(entry);
      if (resolved) return resolved;
    }
    return '';
  }
  if (typeof value === 'object') {
    return (
      value.url ||
      value.src ||
      value.href ||
      value.path ||
      value.thumbnail ||
      value.thumbnailUrl ||
      ''
    );
  }
  return '';
};

const getStoredCheckoutItemSnapshots = () => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = JSON.parse(window.sessionStorage?.getItem(CHECKOUT_ITEM_SNAPSHOT_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch (err) {
    return [];
  }
};

const clearStoredCheckoutItemSnapshots = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage?.removeItem(CHECKOUT_ITEM_SNAPSHOT_KEY);
  } catch (err) {
    // Snapshot storage is optional confirmation-page enrichment.
  }
};

const findCheckoutItemSnapshot = (item, snapshots) => {
  if (!item || !snapshots.length) return null;
  const lineItemId = item?.metadata?.lineItemId;
  const candidates = [
    lineItemId,
    item?.id,
    item?.uniqueId,
  ].filter(Boolean).map((value) => String(value));

  return snapshots.find((snapshot) => {
    const snapshotValues = [
      snapshot?.metadata?.lineItemId,
      snapshot?.id,
      snapshot?.uniqueId,
    ].filter(Boolean).map((value) => String(value));

    return candidates.some((candidate) => snapshotValues.includes(candidate));
  }) || null;
};

const mergeCheckoutItemSnapshot = (item, snapshot) => {
  if (!snapshot) return item;
  return {
    ...snapshot,
    ...item,
    image: getOrderImageValue(item?.image) || getOrderImageValue(snapshot.image),
    images: Array.isArray(item?.images) && item.images.length ? item.images : snapshot.images,
    thumbnailFrontUrl: item?.thumbnailFrontUrl || snapshot.thumbnailFrontUrl,
    thumbnailUrl: item?.thumbnailUrl || snapshot.thumbnailUrl,
    cardImageThumbnailUrl: item?.cardImageThumbnailUrl || snapshot.cardImageThumbnailUrl,
    metadata: {
      ...(snapshot.metadata || {}),
      ...(item?.metadata || {}),
      confirmation: item?.metadata?.confirmation || snapshot.metadata?.confirmation,
      user: item?.metadata?.user || snapshot.metadata?.user,
      delivery: item?.metadata?.delivery || snapshot.metadata?.delivery,
      price: item?.metadata?.price || snapshot.metadata?.price,
      cardSize: item?.metadata?.cardSize || snapshot.metadata?.cardSize,
      envelopeColor: item?.metadata?.envelopeColor || snapshot.metadata?.envelopeColor,
      envelopeType: item?.metadata?.envelopeType || snapshot.metadata?.envelopeType,
      thumbnail: getOrderImageValue(item?.metadata?.thumbnail) || getOrderImageValue(snapshot.metadata?.thumbnail),
      thumbnailUrl: getOrderImageValue(item?.metadata?.thumbnailUrl) || getOrderImageValue(snapshot.metadata?.thumbnailUrl),
    },
  };
};

const getConfirmedSnipcartStateCandidates = () => {
  const state = window.Snipcart?.store?.getState?.();
  return [
    state?.order?.items,
    state?.order?.cart?.items?.items,
    state?.orders?.current?.items,
    state?.orders?.current?.cart?.items?.items,
    window.__ssLastConfirmedCartItems,
  ];
};

const getSnipcartStateItems = ({ confirmedOnly = false } = {}) => {
  try {
    const state = window.Snipcart?.store?.getState?.();
    const snapshots = [
      ...getStoredCheckoutItemSnapshots(),
      ...(Array.isArray(window.__ssLastConfirmedCartItems) ? window.__ssLastConfirmedCartItems : []),
    ];
    const candidates = confirmedOnly
      ? getConfirmedSnipcartStateCandidates()
      : [
          state?.cart?.items?.items,
          state?.cart?.items,
          ...getConfirmedSnipcartStateCandidates(),
        ];
    const items = candidates.find((candidate) => Array.isArray(candidate) && candidate.length) || [];
    return items.map((item) => mergeCheckoutItemSnapshot(item, findCheckoutItemSnapshot(item, snapshots)));
  } catch (err) {
    return [];
  }
};

const captureSnipcartStateItems = ({ allowCartFallback = false } = {}) => {
  if (typeof window === 'undefined') return;
  let items = getSnipcartStateItems({ confirmedOnly: true });
  if (!items.length && allowCartFallback) {
    items = getSnipcartStateItems();
  }
  if (items.length) {
    window.__ssLastConfirmedCartItems = items;
  }
};

const renderOrderValue = (label, value) => {
  if (!value) return '';
  return `
    <div class="ss-order-detail-box">
      <span class="ss-order-detail-label">${escapeHtml(label)}</span>
      <div class="ss-order-detail-value">${escapeHtml(value)}</div>
    </div>
  `;
};

const renderOrderHtmlValue = (label, html) => {
  if (!html) return '';
  return `
    <div class="ss-order-detail-box">
      <span class="ss-order-detail-label">${escapeHtml(label)}</span>
      <div class="ss-order-detail-value">${html}</div>
    </div>
  `;
};

const renderOptionChip = (value) => value
  ? `<span class="ss-order-chip">${escapeHtml(value)}</span>`
  : '';

const uniqueOrderOptions = (options) => {
  const seen = new Set();
  return options.filter((option) => {
    if (!option) return false;
    const key = normalizeText(option).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const ORDER_ICONS = {
  cart: '<svg class="ss-order-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 8h12l-1 11H7L6 8Z" stroke-width="1.8" stroke-linejoin="round"/><path d="M9 8a3 3 0 0 1 6 0" stroke-width="1.8" stroke-linecap="round"/></svg>',
  billing: '<svg class="ss-order-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 21a8 8 0 0 0-16 0" stroke-width="1.8" stroke-linecap="round"/><circle cx="12" cy="7" r="4" stroke-width="1.8"/></svg>',
  payment: '<svg class="ss-order-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="6" width="18" height="12" rx="2" stroke-width="1.8"/><path d="M3 10h18M7 15h4" stroke-width="1.8" stroke-linecap="round"/></svg>',
};

const renderEnhancedOrderItem = (item, fallback = {}) => {
  const metadata = item?.metadata || {};
  const confirmation = metadata.confirmation || {};
  const user = metadata.user || confirmation.deliveryAddress || {};
  const delivery = metadata.delivery || confirmation.delivery || {};
  const confirmationOptions = confirmation.options || {};
  const price = metadata.price || {};
  const thumbnail = normalizeOrderImage(
    getOrderImageValue(item?.image) ||
    item?.thumbnailFrontUrl ||
    item?.thumbnailUrl ||
    item?.cardImageThumbnailUrl ||
    getOrderImageValue(metadata.thumbnailUrl) ||
    getOrderImageValue(metadata.thumbnail) ||
    getOrderImageValue(item?.images)
  );
  const title = item?.name || metadata.title || confirmation.title || fallback.title || 'Greeting card';
  const qty = metadata.actualQuantity || metadata.displayQuantity || confirmation.quantity || price.quantity || item?.quantity || fallback.qty || 1;
  const total = price.total || confirmation.total || item?.totalPrice || item?.total || item?.price || fallback.price;
  const unit = price.unit || item?.unitPrice || item?.price || fallback.price;
  const qtyNumber = Number.parseFloat(String(qty).replace(/[^0-9.-]/g, '')) || 1;
  const unitNumber = parseOrderMoney(price.unit);
  const totalNumber = parseOrderMoney(total);
  const deliveryNumber = parseOrderMoney(delivery.price) || 0;
  const computedTotal = unitNumber !== null ? (unitNumber * qtyNumber) + deliveryNumber : null;
  const displayTotal = (
    computedTotal !== null &&
    deliveryNumber > 0 &&
    (totalNumber === null || totalNumber <= (unitNumber * qtyNumber) + 0.01)
  )
    ? computedTotal
    : (total || unit);
  const address = [
    user.fullName,
    user.address1,
    user.address2,
    [user.locality, user.state, user.postcode].filter(Boolean).join(', '),
    user.country,
  ].filter(Boolean).join('\n');
  const deliveryInfo = [
    delivery.option,
    delivery.deliveryDate ? `Delivery: ${delivery.deliveryDate}` : '',
    delivery.price ? `Delivery cost: ${delivery.price}` : '',
  ].filter(Boolean).join('\n');
  const options = uniqueOrderOptions([
    item?.optionCardSize && `Size: ${item.optionCardSize}`,
    item?.optionEnvelopeColour && `Envelope: ${item.optionEnvelopeColour}`,
    item?.optionEnvelopeType && `Type: ${item.optionEnvelopeType}`,
    metadata.cardSize?.name && `Size: ${metadata.cardSize.name}`,
    metadata.envelopeColor?.name && `Envelope: ${metadata.envelopeColor.name}`,
    metadata.envelopeType?.name && `Type: ${metadata.envelopeType.name}`,
    confirmationOptions.cardSize && `Size: ${confirmationOptions.cardSize}`,
    confirmationOptions.envelopeColor && `Envelope: ${confirmationOptions.envelopeColor}`,
    confirmationOptions.envelopeType && `Type: ${confirmationOptions.envelopeType}`,
    metadata.offer?.title && `Offer: ${metadata.offer.title}${metadata.offer.value ? ` ${metadata.offer.value}` : ''}`,
  ]);
  const deliveryAddressText = address || 'Delivery address confirmed';
  const deliveryInfoText = deliveryInfo || 'Delivery details confirmed';

  return `
    <article class="ss-order-item">
      ${thumbnail
        ? `<img class="ss-order-thumb" src="${escapeHtml(thumbnail)}" alt="${escapeHtml(title)} thumbnail" />`
        : '<div class="ss-order-thumb ss-order-thumb-placeholder">No image</div>'
      }
      <div>
        <h3 class="ss-order-item-title">${escapeHtml(title)}</h3>
        <div class="ss-order-detail-grid">
          ${renderOrderValue('Delivery address', deliveryAddressText)}
          ${renderOrderValue('Delivery information', deliveryInfoText)}
        </div>
        <div class="ss-order-options">
          ${options.length ? options.map(renderOptionChip).join('') : renderOptionChip('Options confirmed')}
        </div>
      </div>
      <div class="ss-order-price-stack">
        <strong class="ss-order-item-price">${escapeHtml(formatOrderMoney(displayTotal))}</strong>
        <span class="ss-order-price-label">Qty: ${escapeHtml(qty)}</span>
      </div>
    </article>
  `;
};

const isFallbackSummaryRowText = (text) => {
  if (!text || /not available in order data/i.test(text)) return false;
  if (/^cart summary$/i.test(text)) return false;
  return /qty:\s*\d+/i.test(text) && /(?:\$|SGD)\s*\d/i.test(text);
};

const collectFallbackSummaryRows = (node, rows = []) => {
  if (!node || node.closest?.('.ss-order-custom')) return rows;

  const text = normalizeText(node.textContent);
  if (!isFallbackSummaryRowText(text)) return rows;

  const childRows = Array.from(node.children || []).filter((child) => (
    !child.closest?.('.ss-order-custom') &&
    isFallbackSummaryRowText(normalizeText(child.textContent))
  ));

  if (!childRows.length) {
    rows.push(node);
    return rows;
  }

  childRows.forEach((child) => collectFallbackSummaryRows(child, rows));
  return rows;
};

const getFallbackSummaryItems = (summarySection) => {
  if (!summarySection) return [];
  const rows = collectFallbackSummaryRows(summarySection);

  return rows.map((row) => {
    const text = normalizeText(row?.textContent || '');
    const title = text
      .replace(/^Cart summary\s*/i, '')
      .replace(/Qty:\s*\d+.*/i, '')
      .trim();
    return {
      title,
      qty: (text.match(/Qty:\s*(\d+)/i) || [])[1],
      price: (text.match(/((?:\$|SGD)\s*\d+(?:\.\d{2})?)/i) || [])[1],
    };
  }).filter((item) => {
    return Boolean(item.title);
  });
};

const getFallbackSummaryItem = (summarySection) => getFallbackSummaryItems(summarySection)[0] || {};

const getOrderItemName = (item) => (
  item?.name || item?.metadata?.title || item?.metadata?.confirmation?.title || ''
);

const getOrderItemPrice = (item) => (
  item?.metadata?.price?.total ||
  item?.metadata?.confirmation?.total ||
  item?.totalPrice ||
  item?.total ||
  item?.price
);

const isSameFallbackOrderItem = (item, fallbackItem) => {
  if (!fallbackItem?.title) return false;
  const name = normalizeText(getOrderItemName(item)).toLowerCase();
  const fallbackTitle = normalizeText(fallbackItem.title).toLowerCase();
  if (!name || name !== fallbackTitle) return false;

  const fallbackPrice = parseOrderMoney(fallbackItem.price);
  if (fallbackPrice === null) return true;
  const price = parseOrderMoney(getOrderItemPrice(item));
  return price === null || Math.abs(price - fallbackPrice) < 0.01;
};

const getSnapshotFallbackItems = (fallbackItems) => {
  const visibleItems = Array.isArray(fallbackItems) ? fallbackItems : [fallbackItems].filter(Boolean);
  if (!visibleItems.length) return [];
  const snapshots = [
    ...getStoredCheckoutItemSnapshots(),
    ...(Array.isArray(window.__ssLastConfirmedCartItems) ? window.__ssLastConfirmedCartItems : []),
  ].filter((item) => {
    const name = getOrderItemName(item);
    return item && name && !/shipping|delivery/i.test(name);
  });
  if (!snapshots.length) return [];

  const used = new Set();
  return visibleItems.map((fallbackItem) => {
    for (let index = snapshots.length - 1; index >= 0; index -= 1) {
      if (used.has(index)) continue;
      if (!isSameFallbackOrderItem(snapshots[index], fallbackItem)) continue;
      used.add(index);
      return snapshots[index];
    }
    return fallbackItem;
  });
};

const getFallbackSectionText = (section) => {
  if (!section) return '';
  const clone = section.cloneNode(true);
  clone.querySelectorAll('.ss-order-custom').forEach((element) => element.remove());
  return normalizeText(clone.textContent);
};

const extractInvoiceNumber = (text) => (
  normalizeText(text).match(/Invoice number\s*:?\s*([0-9]{5,})(?![0-9])/i) || []
)[1];

const splitBillingText = (billingText) => ({
  customer: billingText
    .split(/Billing address/i)[0]
    .replace(/^Customer information/i, '')
    .trim(),
  address: (billingText.match(/Billing address\s*(.*)$/i) || [])[1]?.trim() || '',
});

const sanitizeBillingFallback = (billing) => {
  const customer = billing?.customer || '';
  const address = billing?.address || '';
  const polluted = /cart summary|payment|subtotal|total|qty:/i.test(`${customer} ${address}`);
  return polluted
    ? { customer: '', address: '' }
    : { customer, address };
};

const renderPaymentMethod = (paymentText) => {
  const cleanText = normalizeText(paymentText)
    .replace(/\b(Subtotal|Total)\b.*$/i, '')
    .trim();
  const brand = (cleanText.match(/\b(Visa|Mastercard|MasterCard|Amex|American Express|PayPal)\b/i) || [])[1];
  const lastFour = (cleanText.match(/(?:\*|•|\.|\s)(\d{4})\b/) || cleanText.match(/\b(\d{4})\b(?!.*\b\d{4}\b)/) || [])[1];
  const displayBrand = brand ? brand.replace(/mastercard/i, 'Mastercard').replace(/american express/i, 'Amex') : 'Card';
  const masked = lastFour ? `•••• ${lastFour}` : 'Details stored securely';

  return `
    <div class="ss-order-card-method">
      <span class="ss-order-card-badge">${escapeHtml(displayBrand)}</span>
      <span>${escapeHtml(masked)}</span>
    </div>
  `;
};

const formatOrderHeaderText = (headerSection, invoiceNumber) => {
  if (!headerSection || !invoiceNumber) return;

  let invoiceLine = headerSection.querySelector('.ss-order-invoice-line-custom');
  if (!invoiceLine) {
    invoiceLine = document.createElement('p');
    invoiceLine.className = 'ss-order-invoice-line ss-order-invoice-line-custom';
    const message = headerSection.querySelector('.ss-order-header-message');
    if (message) {
      headerSection.insertBefore(invoiceLine, message);
    } else {
      headerSection.appendChild(invoiceLine);
    }
  }
  invoiceLine.innerHTML = `Invoice number: <strong class="ss-order-invoice-number">${escapeHtml(invoiceNumber)}</strong>`;

  Array.from(headerSection.querySelectorAll('*')).forEach((element) => {
    if (
      element.closest('.ss-order-header-message') ||
      element.closest('.ss-order-invoice-line-custom') ||
      element.classList.contains('ss-order-invoice-line-custom')
    ) {
      return;
    }
    const text = normalizeText(element.textContent);
    if (
      /^Invoice number\s*:?\s*[A-Z0-9-]*$/i.test(text) ||
      text === invoiceNumber
    ) {
      element.classList.add('ss-order-invoice-native-hidden');
    }
  });
};

const findVisibleTextElement = (root, matcher) => {
  if (!root) return null;
  const elements = Array.from(root.querySelectorAll('*'))
    .filter((element) => {
      if (element.closest('.ss-order-custom')) return false;
      const text = normalizeText(element.textContent);
      if (!text || !matcher(text)) return false;
      const rect = element.getBoundingClientRect?.();
      return rect && rect.width > 0 && rect.height > 0;
    })
    .sort((a, b) => {
      const aRect = a.getBoundingClientRect();
      const bRect = b.getBoundingClientRect();
      return (aRect.width * aRect.height) - (bRect.width * bRect.height);
    });
  return elements[0] || null;
};

const directSectionUnderFrame = (element, frame) => {
  if (!element || !frame || !frame.contains(element)) return null;
  let current = element;
  while (current?.parentElement && current.parentElement !== frame) {
    current = current.parentElement;
  }
  return current?.parentElement === frame ? current : null;
};

const markOrderConfirmationPanels = () => {
  if (typeof document === 'undefined') return;
  if (!isSnipcartOrderRoute()) {
    endOrderHydrationMask();
  }
  const root = document.getElementById('snipcart-main-container') || document.getElementById('snipcart');
  if (!root) return;

  root.querySelectorAll('.ss-order-custom').forEach((element) => element.remove());
  root.querySelectorAll('.ss-order-frame').forEach((element) => {
    element.classList.remove('ss-order-frame');
  });
  root.querySelectorAll('.ss-order-section').forEach((element) => {
    element.classList.remove(
      'ss-order-section',
      'ss-order-header',
      'ss-order-summary',
      'ss-order-billing',
      'ss-order-payment',
      'ss-order-totals'
    );
  });
  root.querySelectorAll('.ss-order-summary-item').forEach((element) => {
    element.classList.remove('ss-order-summary-item');
  });

  const thank = findVisibleTextElement(root, (text) => /^thank you for your order/i.test(text));
  const summary = findVisibleTextElement(root, (text) => /^cart summary$/i.test(text));
  const billing = findVisibleTextElement(root, (text) => /^billing$/i.test(text));
  const payment = findVisibleTextElement(root, (text) => /^payment$/i.test(text));
  const subtotal = findVisibleTextElement(root, (text) => /^subtotal$/i.test(text));
  const total = findVisibleTextElement(root, (text) => /^total$/i.test(text));

  if (!thank || !summary || !billing || !payment) return;

  const required = [thank, summary, billing, payment, subtotal, total].filter(Boolean);
  let frame = thank;
  while (frame && frame !== root && !required.every((node) => frame.contains(node))) {
    frame = frame.parentElement;
  }
  if (!frame || frame === root) return;

  frame.classList.add('ss-order-frame');

  [
    [thank, 'ss-order-header'],
    [summary, 'ss-order-summary'],
    [billing, 'ss-order-billing'],
    [payment, 'ss-order-payment'],
  ].forEach(([marker, className]) => {
    const section = directSectionUnderFrame(marker, frame);
    if (section) {
      section.classList.add('ss-order-section', className);
    }
  });

  const summarySection = directSectionUnderFrame(summary, frame);
  if (summarySection) {
    Array.from(summarySection.querySelectorAll('*')).forEach((element) => {
      const text = normalizeText(element.textContent);
      if (/qty:\s*\d+/i.test(text) && /\$\s*\d/.test(text)) {
        element.classList.add('ss-order-summary-item');
      }
    });
  }

  if (subtotal && total) {
    let totals = subtotal.parentElement;
    while (totals && totals !== frame && !totals.contains(total)) {
      totals = totals.parentElement;
    }
    if (totals && totals !== frame) {
      const totalsSection = directSectionUnderFrame(totals, frame) || totals;
      totalsSection.classList.add('ss-order-section', 'ss-order-totals');
    }
  }

  const headerSection = directSectionUnderFrame(thank, frame);
  const billingSection = directSectionUnderFrame(billing, frame);
  const paymentSection = directSectionUnderFrame(payment, frame);
  const invoiceNumber = extractInvoiceNumber([
    getFallbackSectionText(headerSection),
    getFallbackSectionText(frame),
  ].join(' '));
  formatOrderHeaderText(headerSection, invoiceNumber);
  if (headerSection && !headerSection.querySelector('.ss-order-header-message')) {
    const message = document.createElement('p');
    message.className = 'ss-order-header-message';
    message.innerHTML = 'Thank you! We are now preparing your greeting cards and will keep you informed as delivery progresses. Questions? Contact us at&nbsp;<a href="mailto:hello@simplysay.com">hello@simplysay.com</a>';
    headerSection.appendChild(message);
  }
  const fallbackItems = getFallbackSummaryItems(summarySection);
  const fallbackItem = fallbackItems[0] || {};
  const stateItems = getSnipcartStateItems({ confirmedOnly: true }).filter((item) => {
    const name = item?.name || item?.metadata?.title || '';
    return item && !/shipping|delivery/i.test(name);
  });
  const snapshotFallbackItems = fallbackItems.length > stateItems.length
    ? getSnapshotFallbackItems(fallbackItems)
    : [];
  const items = stateItems.length && stateItems.length >= fallbackItems.length
    ? stateItems
    : (snapshotFallbackItems.length
      ? snapshotFallbackItems
      : fallbackItems);
  const subtotalText = getFallbackSectionText(subtotal?.parentElement);
  const totalText = getFallbackSectionText(total?.parentElement);
  const subtotalValue = (subtotalText.match(/(?:Subtotal)\s*(SGD\s*)?(\$?\s*\d+(?:\.\d{2})?)/i) || [])[0]?.replace(/^Subtotal\s*/i, '') || '';
  const totalValue = (totalText.match(/(?:Total)\s*(SGD\s*)?(\$?\s*\d+(?:\.\d{2})?)/i) || [])[0]?.replace(/^Total\s*/i, '') || '';
  const billingText = getFallbackSectionText(billingSection).replace(/^Billing\s*/i, '');
  const fallbackBilling = sanitizeBillingFallback(splitBillingText(billingText));
  const customerText = fallbackBilling.customer;
  const addressText = fallbackBilling.address;
  const paymentText = getFallbackSectionText(paymentSection).replace(/^Payment\s*/i, '');

  const custom = document.createElement('div');
  custom.className = 'ss-order-custom';
  if (headerSection?.nextSibling) {
    frame.insertBefore(custom, headerSection.nextSibling);
  } else {
    frame.appendChild(custom);
  }

  custom.innerHTML = `
    <section class="ss-order-custom-section">
      <h2 class="ss-order-custom-heading">${ORDER_ICONS.cart}<span>Summary</span></h2>
      ${items.length
        ? items.map((item, index) => renderEnhancedOrderItem(item, fallbackItems[index] || fallbackItem)).join('')
        : '<p class="ss-order-detail-value">Your order has been received. Full item details will also be included in your confirmation email.</p>'
      }
    </section>
    <section class="ss-order-custom-section">
      <h2 class="ss-order-custom-heading">${ORDER_ICONS.billing}<span>Billing</span></h2>
      <div class="ss-order-two-col">
        ${renderOrderValue('Customer information', customerText || 'Billing details confirmed')}
        ${renderOrderValue('Billing address', addressText || 'Billing address confirmed')}
      </div>
    </section>
    <section class="ss-order-custom-section">
      <h2 class="ss-order-custom-heading">${ORDER_ICONS.payment}<span>Payment</span></h2>
      ${renderOrderHtmlValue('Payment method', renderPaymentMethod(paymentText)) || renderOrderValue('Payment method', 'Payment details not available in order data')}
    </section>
    <section class="ss-order-custom-section ss-order-total-section">
      <div class="ss-order-total-row"><span>Subtotal</span><strong>${escapeHtml(subtotalValue || totalValue || '')}</strong></div>
      <div class="ss-order-total-row"><span>Total</span><strong>${escapeHtml(totalValue || subtotalValue || '')}</strong></div>
    </section>
  `;
  endOrderHydrationMask();
};

const schedulePillUpdate = () => {
  if (typeof window === 'undefined') return;
  ensureOrderStylesheet();
  beginOrderHydrationMask();
  registerSnipcartPaymentStyles();
  markSnipcartPaymentPanels();
  markOrderConfirmationPanels();
  hideSnipcartEmptyBackButton();
  applyPillStyles();
  attachSnipcartObserver();
  requestAnimationFrame(() => {
    markSnipcartPaymentPanels();
    markOrderConfirmationPanels();
    hideSnipcartEmptyBackButton();
    applyPillStyles();
  });
  setTimeout(() => {
    registerSnipcartPaymentStyles();
    markSnipcartPaymentPanels();
    markOrderConfirmationPanels();
    hideSnipcartEmptyBackButton();
    applyPillStyles();
    attachSnipcartObserver();
  }, 100);
  setTimeout(() => {
    registerSnipcartPaymentStyles();
    markSnipcartPaymentPanels();
    markOrderConfirmationPanels();
    hideSnipcartEmptyBackButton();
    applyPillStyles();
    attachSnipcartObserver();
  }, 400);
  setTimeout(() => {
    registerSnipcartPaymentStyles();
    markSnipcartPaymentPanels();
    markOrderConfirmationPanels();
    hideSnipcartEmptyBackButton();
    applyPillStyles();
    attachSnipcartObserver();
  }, 1000);
};

document.addEventListener('snipcart.ready', () => {
  suppressSnipcartDuplicateCartNavigation();
  ensureOrderStylesheet();
  registerSnipcartPaymentStyles();
  markSnipcartPaymentPanels();
  schedulePillUpdate();

  const { Snipcart } = window;
  if (Snipcart?.events) {
    const events = ['cart.opened', 'cart.closed', 'cart.confirmed', 'theme.routechanged'];
    events.forEach((evt) => {
      Snipcart.events.on(evt, () => {
        if (evt === 'cart.confirmed') {
          captureSnipcartStateItems({ allowCartFallback: true });
        }
        schedulePillUpdate();
      });
    });
    Snipcart.events.on('order.completed', () => {
      captureSnipcartStateItems({ allowCartFallback: true });
      schedulePillUpdate();
    });
  }
  if (Snipcart?.store) {
    Snipcart.store.subscribe(schedulePillUpdate);
  }
});

export const wrapRootElement = ({ element }) => (
    <UserContextProvider>{element}</UserContextProvider>
  )

const GATSBY_SCROLL_KEY_PREFIX = '@@scroll|';

const isWorkflowRoutePath = pathname => (
  typeof pathname === 'string' &&
  pathname
    .split('?')[0]
    .split('/')
    .filter(Boolean).length >= 3
);

const isWorkflowListingRoutePath = pathname => {
  if (typeof pathname !== 'string') return false;
  const normalized = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  if (normalized === '/search') return true;
  if (!normalized.startsWith('/greeting-cards')) return false;
  return !isWorkflowRoutePath(normalized);
};

const disableSavedScrollRestoration = () => {
  if (typeof window === 'undefined') return;

  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  try {
    Object.keys(window.sessionStorage)
      .filter(key => key.startsWith(GATSBY_SCROLL_KEY_PREFIX))
      .forEach(key => window.sessionStorage.removeItem(key));
  } catch (err) {
    // Ignore browsers that block sessionStorage access.
  }
};

const scrollPageToTop = location => {
  const hasWorkflowReturnScroll =
    typeof location?.state?.workflowReturnScrollY === 'number';
  if (
    typeof window === 'undefined' ||
    location?.hash ||
    (hasWorkflowReturnScroll && isWorkflowListingRoutePath(location?.pathname))
  ) {
    return;
  }

  const reset = () => {
    if ((window.scrollY || window.pageYOffset || 0) > 0) {
      window.scrollTo(0, 0);
    }
  };

  reset();
  window.requestAnimationFrame(reset);
  window.setTimeout(reset, 0);
};

const restoreWorkflowReturnScroll = location => {
  if (typeof window === 'undefined') return;
  if (!isWorkflowListingRoutePath(location?.pathname)) {
    return;
  }
  const scrollY = location?.state?.workflowReturnScrollY;
  if (typeof scrollY !== 'number' || Number.isNaN(scrollY) || scrollY < 0) {
    return;
  }

  const restore = () => {
    const scrollHeight = Math.max(
      document.documentElement ? document.documentElement.scrollHeight : 0,
      document.body ? document.body.scrollHeight : 0
    );
    const maxScrollable = Math.max(0, scrollHeight - window.innerHeight);
    window.scrollTo(0, Math.min(scrollY, maxScrollable));
  };

  restore();
  window.requestAnimationFrame(restore);
  window.setTimeout(restore, 100);
  window.setTimeout(restore, 400);
};

const persistWorkflowListingRoute = location => {
  if (typeof window === 'undefined') return;
  const pathname = location?.pathname || '';
  const search = location?.search || '';
  localstorageHelper.saveWorkflowLastListingPath(
    `${pathname}${search}`,
    window.scrollY || window.pageYOffset || 0
  );
};

export const onClientEntry = () => {
  disableSavedScrollRestoration();
  ensureOrderStylesheet();
  beginOrderHydrationMask();
};

export const onInitialClientRender = () => {
  disableSavedScrollRestoration();
  ensureOrderStylesheet();
  beginOrderHydrationMask();
  scrollPageToTop(window.location);
};

export const onRouteUpdate = ({ location }) => {
  disableSavedScrollRestoration();
  ensureOrderStylesheet();
  schedulePillUpdate();
  persistWorkflowListingRoute(location);
  scrollPageToTop(location);
  restoreWorkflowReturnScroll(location);
};

export const shouldUpdateScroll = ({ prevRouterProps, routerProps: { location } }) => {
  const hash = location?.hash || '';
  if (hash) {
    return true;
  }
  return Boolean(prevRouterProps);
};
